import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/components/ui/card";
import { Button } from "@shared/components/ui/button";
import { Badge } from "@shared/components/ui/badge";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@shared/components/ui/table";
import { AlertTriangle, Package, TrendingDown, User, Edit } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getLowStockProductsWithMovements } from '@features/products/services/productService';
import { format } from "date-fns";
import { StockEditDialog } from "./StockEditDialog";

const adjustmentTypeColors = {
  manual: "bg-chart-1/10 text-chart-1 border-chart-1/20",
  pickup: "bg-chart-2/10 text-chart-2 border-chart-2/20",
  package: "bg-destructive/10 text-destructive border-destructive/20",
  correction: "bg-chart-5/10 text-chart-5 border-chart-5/20",
};

export function LowStockDashboard() {
  const { t } = useTranslation();
  const [threshold, setThreshold] = useState(10);

  const {
    data: lowStockData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["lowStockWithMovements", threshold],
    queryFn: () => getLowStockProductsWithMovements(threshold),
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  const handleThresholdChange = (newThreshold) => {
    setThreshold(newThreshold);
  };

  const renderLastMovement = (lastMovement) => {
    if (!lastMovement) {
      return (
        <div className="text-sm text-muted-foreground">
          {t("products.noRecentMovement")}
        </div>
      );
    }

    const colorClass =
      adjustmentTypeColors[lastMovement.adjustmentType] ||
      "bg-gray-100 text-gray-800";

    return (
      <div className="space-y-1">
        <Badge variant="secondary" className={`${colorClass} text-xs`}>
          {t(`products.adjustmentTypes.${lastMovement.adjustmentType}`)}
        </Badge>
        <div className="text-xs text-muted-foreground">
          {format(new Date(lastMovement.createdAt), "MMM dd, yyyy HH:mm")}
        </div>
        <div className="text-xs">
          {lastMovement.adjustment > 0 ? "+" : ""}
          {lastMovement.adjustment}
          {lastMovement.user?.name && ` by ${lastMovement.user.name}`}
        </div>
      </div>
    );
  };

  const getStockStatusColor = (stock) => {
    if (stock === 0) return "text-destructive bg-destructive/10";
    if (stock <= 5) return "text-chart-3 bg-chart-3/10";
    return "text-chart-4 bg-chart-4/10";
  };

  const getStockStatusIcon = (stock) => {
    if (stock === 0) return <AlertTriangle className="w-4 h-4 text-destructive" />;
    if (stock <= 5) return <TrendingDown className="w-4 h-4 text-chart-3" />;
    return <Package className="w-4 h-4 text-chart-4" />;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">
            {t("products.lowStockDashboard")}
          </h2>
          <p className="text-muted-foreground">
            {t("products.lowStockDashboardDescription")}
          </p>
        </div>
        <Button onClick={() => refetch()} variant="outline">
          {t("common.refresh")}
        </Button>
      </div>

      {/* Threshold Control */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {t("products.stockThreshold")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="flex-1 max-w-xs">
              <Label htmlFor="threshold">
                {t("products.lowStockThreshold")}
              </Label>
              <Input
                id="threshold"
                type="number"
                min="0"
                value={threshold}
                onChange={(e) =>
                  handleThresholdChange(parseInt(e.target.value) || 0)
                }
                placeholder="10"
              />
            </div>
            <div className="text-sm text-muted-foreground">
              {t("products.productsWithStockBelow", { threshold })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Low Stock Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t("products.totalLowStock")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-chart-3">
              {lowStockData?.data?.length || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t("products.outOfStock")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">
              {lowStockData?.data?.filter((p) => p.stock === 0).length || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t("products.criticalStock")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-chart-3">
              {lowStockData?.data?.filter((p) => p.stock > 0 && p.stock <= 5)
                .length || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              {t("products.stockLessThan5")}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t("products.withRecentMovements")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-chart-1">
              {lowStockData?.data?.filter((p) => p.lastMovement).length || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Low Stock Products Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-chart-3" />
            {t("products.lowStockProducts")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {Array(5)
                .fill(0)
                .map((_, i) => (
                  <div
                    key={i}
                    className="h-16 bg-muted animate-pulse rounded"
                  />
                ))}
            </div>
          ) : lowStockData?.data?.length > 0 ? (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("products.product")}</TableHead>
                    <TableHead>{t("products.currentStock")}</TableHead>
                    <TableHead>{t("products.price")}</TableHead>
                    <TableHead>{t("products.owner")}</TableHead>
                    <TableHead>{t("products.lastMovement")}</TableHead>
                    <TableHead>{t("common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lowStockData.data.map((product) => (
                    <TableRow key={product._id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {product.image ? (
                            <img
                              src={`/api/uploads/products/${product.image}`}
                              alt={product.name}
                              className="w-10 h-10 rounded object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 bg-muted rounded flex items-center justify-center">
                              <Package className="w-5 h-5 text-muted-foreground" />
                            </div>
                          )}
                          <div>
                            <div className="font-medium">{product.name}</div>
                            <div className="text-sm text-muted-foreground">
                              ID: {product._id.slice(-6)}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div
                          className={`inline-flex items-center gap-2 px-2 py-1 rounded-md ${getStockStatusColor(
                            product.stock
                          )}`}
                        >
                          {getStockStatusIcon(product.stock)}
                          <span className="font-medium">{product.stock}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">${product.price}</div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-muted-foreground" />
                          <div>
                            <div className="font-medium">
                              {product.user?.name}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {product.user?.email}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {renderLastMovement(product.lastMovement)}
                      </TableCell>
                      <TableCell>
                        <StockEditDialog
                          product={product}
                          trigger={
                            <Button variant="outline" size="sm">
                              <Edit className="w-4 h-4 mr-2" />
                              {t("products.updateStock")}
                            </Button>
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8">
              <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">
                {t("products.noLowStockProducts")}
              </h3>
              <p className="text-muted-foreground">
                {t("products.allProductsAboveThreshold", { threshold })}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
